import { useState } from 'react';
import type { ActionActivityEntry } from '../types';

function fmtWhen(at: string): string {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return at;
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** An Action's append-only activity timeline — human notes and system
 *  events interleaved, oldest first (how they're stored), plus an add-a-
 *  note box for anyone permitted to comment (owner, participants, admin/
 *  Key 3). Mirrors TaskNotes' look, but distinguishes the two kinds: a
 *  system event reads as a quiet third-person line ("changed status from
 *  Open to Waiting."), a note is quoted and more prominent. */
export default function ActionActivityFeed({
  activity,
  canAdd,
  onAddNote,
}: {
  activity: ActionActivityEntry[];
  canAdd: boolean;
  onAddNote: (text: string) => Promise<void>;
}) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  return (
    <div>
      {activity.length === 0 && <div className="subtitle">No activity yet.</div>}
      {activity.map((e, i) => (
        <div key={i} className="action-activity-row">
          <div className="subtitle">
            {fmtWhen(e.at)} — {e.by.split('@')[0]}
          </div>
          {e.kind === 'note' ? (
            <div className="action-activity-note">“{e.text}”</div>
          ) : (
            <div className="action-activity-event">{e.text}</div>
          )}
        </div>
      ))}
      {canAdd && (
        <>
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Add a note"
              style={{ flex: 1 }}
            />
            <button
              className="btn"
              disabled={busy || !text.trim()}
              onClick={async () => {
                setBusy(true);
                setErr(null);
                try {
                  await onAddNote(text.trim());
                  setText('');
                } catch (e) {
                  setErr(e instanceof Error ? e.message : 'Could not add that note.');
                } finally {
                  setBusy(false);
                }
              }}
            >
              Add
            </button>
          </div>
          {err && <div className="err">{err}</div>}
        </>
      )}
    </div>
  );
}
